import { useEffect, useMemo, useState } from 'react';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import { IInvoiceStatusType, IInvoiceType } from '@/types';

import { PrefundReturnStatus } from '../finops.types';
import { useGetEoyReconStatusQuery } from '../queries/useGetEoyReconStatus.query';
import { useGetPrefundReturnStatusQuery } from '../queries/useGetPrefundReturnStatus.query';

interface Props {
  type: IInvoiceType;
  status: IInvoiceStatusType;
  invoiceId?: string;
  initialAmount?: number;
}
export const useIsInvoiceNeedApprove = ({ type, status, initialAmount, invoiceId }: Props) => {
  const [amount, setAmount] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const hasReturnFundsAccess = useHasAccess([{ permission: Permissions.RETURN_FUNDS_TO_ORGANIZATION }]);
  const isNeedPrefundStatusCheck = useMemo(() => {
    const isPrefundReturn =
      type === IInvoiceType.ORGANIZATION_LEVEL_PREFUND_REPLENISHMENT_RETURN ||
      type === IInvoiceType.PLAN_LEVEL_PREFUND_REPLENISHMENT_RETURN;
    const isPaymentRequested = status === IInvoiceStatusType.ACTION_REQUIRED;

    if (isPrefundReturn && isPaymentRequested) return true;
    return false;
  }, [status, type]);
  const isNeedEoyReconStatusCheck = useMemo(() => {
    const isEoyRecon = type === IInvoiceType.EOY_PLAN_RECONCILIATION;
    const isPaymentRequested = status === IInvoiceStatusType.ACTION_REQUIRED;

    if (isEoyRecon && isPaymentRequested) return true;
    return false;
  }, [status, type]);

  const { prefundReturnStatus, refetch: prefundRefetch } = useGetPrefundReturnStatusQuery(
    invoiceId,
    isNeedPrefundStatusCheck
  );
  const {
    eoyReturnStatus,
    eoyReconPlanName,
    refetch: eoyRefetch,
  } = useGetEoyReconStatusQuery(invoiceId, isNeedEoyReconStatusCheck);

  const isPrefundInvoiceNeedApprove = useMemo(() => {
    if (!hasReturnFundsAccess) return false;
    if (!isNeedPrefundStatusCheck) return false;
    if (prefundReturnStatus === null) return false;
    if (prefundReturnStatus !== PrefundReturnStatus.IN_PROGRESS) return false;
    return true;
  }, [hasReturnFundsAccess, isNeedPrefundStatusCheck, prefundReturnStatus]);

  const isEoyReconInvoiceNeedApprove = useMemo(() => {
    if (!hasReturnFundsAccess) return false;
    if (!isNeedEoyReconStatusCheck) return false;
    if (eoyReturnStatus === null) return false;
    if (eoyReturnStatus !== PrefundReturnStatus.WAITING_FOR_APPROVAL) return false;
    return true;
  }, [hasReturnFundsAccess, isNeedEoyReconStatusCheck, eoyReturnStatus]);

  const isError = useMemo(() => {
    const isAmountPresent = Boolean(amount && !isNaN(Number(amount)));
    if (isSubmitted && !isAmountPresent) {
      return true;
    }
    return false;
  }, [isSubmitted, amount]);

  const currentRefetch = useMemo(() => {
    if (isPrefundInvoiceNeedApprove) return prefundRefetch;
    if (isEoyReconInvoiceNeedApprove) return eoyRefetch;
    return null;
  }, [isPrefundInvoiceNeedApprove, isEoyReconInvoiceNeedApprove, prefundRefetch, eoyRefetch]);

  useEffect(() => {
    if (initialAmount) {
      setAmount(`${initialAmount}`);
    }
  }, [initialAmount]);

  return {
    isInvoiceNeedApprove: isPrefundInvoiceNeedApprove || isEoyReconInvoiceNeedApprove,
    eoyReconPlanName,
    amount,
    setAmount,
    refetchStatus: currentRefetch,
    isSubmitted,
    setIsSubmitted,
    isError,
    isSuccess,
    setIsSuccess,
  };
};
